import { Box } from '@mantine/core';
import { useChartPresignDownload } from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import { memo, useEffect, useRef, useState } from 'react';

// Chart renderer component that uses a chart ID to fetch and display a chart
export interface ChartRendererProps {
	chartId: string;
}

// Modify the HTML to ensure the chart fills the entire iframe
const STYLE_TAG = `
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap">
<style>
		html, body {
				margin: 0;
				padding: 0;
				height: 100%;
				width: 100%;
				font-family: 'Inter', sans-serif;
				max-height: calc(100% - 2px);
		}
		#vis.vega-embed {
				height: 100% !important;
				padding: 0 !important;
		}
		.marks {
				width: 100% !important;
				height: 100% !important;
		}
		.chart-wrapper {
				width: 100% !important;
				height: 100% !important;
		}
		/* Add rounded corners to the topmost bar only */
		.mark-rect.role-mark path[aria-label*="category: bar1"] {
				clip-path: inset(0% 0% 0% 0% round 4px 4px 0px 0px);
		}
		/* Remove border radius from the "click to view actions" button */
		details, details summary, details svg {
				border-radius: 0 !important;
				border: none !important;
				outline: none !important;
				box-shadow: none !important;
		}
</style>`;

export const ChartRenderer = memo(({ chartId }: ChartRendererProps) => {
	const [blobUrl, setBlobUrl] = useState<string | null>(null);
	const iframeRef = useRef<HTMLIFrameElement>(null);

	const {
		data: presignedUrl,
		isLoading,
		error,
	} = useChartPresignDownload({
		queryParams: { chart_id: chartId },
	});

	useEffect(() => {
		let isMounted = true;
		let objectUrl: string | null = null;

		// If presigned URL isn't available yet, don't do anything
		if (!presignedUrl || isLoading || error) return;

		// Fetch the HTML content from the S3 URL and create a blob URL
		const fetchAndCreateBlobUrl = async () => {
			try {
				// Fetch the HTML content from the S3 URL
				const response = await fetch(presignedUrl);
				if (!response.ok) return;

				// Get the HTML content as text
				let htmlContent = await response.text();

				// Insert the style tag
				if (htmlContent.includes('</head>')) {
					htmlContent = htmlContent.replace('</head>', `${STYLE_TAG}</head>`);
				} else {
					htmlContent = htmlContent.replace('<body', `${STYLE_TAG}<body`);
				}

				// Create a Blob from the modified HTML content
				const blob = new Blob([htmlContent], { type: 'text/html' });

				// Create a URL for the Blob
				objectUrl = URL.createObjectURL(blob);

				if (isMounted) {
					setBlobUrl(objectUrl);
				}
			} catch (err) {
				// Just silently fail - we don't want to show errors
			}
		};

		void fetchAndCreateBlobUrl();

		// Cleanup function to revoke the blob URL and prevent memory leaks
		return () => {
			isMounted = false;
			if (objectUrl) {
				URL.revokeObjectURL(objectUrl);
			}
		};
	}, [presignedUrl, isLoading, error]);

	if (error) {
		return (
			<Text size="sm" color="text/critical/default">
				Error loading chart
			</Text>
		);
	}

	// If we don't have a blob URL, don't render anything
	if (!blobUrl) return null;

	return (
		<Box
			mt="md"
			mb="md"
			sx={{
				position: 'relative',
				resize: 'vertical',
				overflow: 'hidden',
				minHeight: '400px',
				maxHeight: '800px',
				height: '500px',
				border: '1px solid #eee',
				borderRadius: '4px',
				padding: '8px',
				'&::after': {
					content: '""',
					position: 'absolute',
					bottom: 1,
					left: '50%',
					transform: 'translateX(-50%)',
					width: 30,
					height: 3,
					backgroundColor: '#ccc',
					borderRadius: 2,
					pointerEvents: 'none',
				},
			}}
		>
			<iframe
				ref={iframeRef}
				src={blobUrl}
				style={{
					width: '100%',
					height: '100%',
					border: 'none',
					display: 'block',
				}}
				sandbox="allow-scripts allow-same-origin allow-downloads"
				title="Chart Visualization"
			/>
		</Box>
	);
});
